import { activeFilterCount, type DeckFilterState } from './deckFilterState.ts';
import type { DeckSummary } from '../../../shared/deck.ts';

/**
 * "Jump back in" — which decks the strip above the grid shows, and whether it
 * shows at all.
 *
 * The strip is read-only navigation: a row of the decks you were last in, so
 * getting back to one costs a click rather than a scroll. Everything that is
 * easy to get wrong about it — the order, the cap, and the four situations
 * where it must not appear — is decided here, without a DOM, so the component
 * next door is a renderer.
 */

/**
 * How many decks the strip holds.
 *
 * Six because the row has to fit on one line at a laptop width without turning
 * into a second grid: past that it stops being a shortcut and starts being a
 * duplicate of the body underneath it. On a phone the row scrolls sideways
 * rather than growing, so the cap is the same number everywhere.
 */
export const RECENT_STRIP_LIMIT = 6;

type RecentStripInput = {
  /** The decks the grid can show — `visibleDecks` from `useDeckFilters`, so
   *  banks are already out. Soft-deleted decks are not in this list at all:
   *  the trash is its own payload (`GET /api/trash`), which is why there is no
   *  trashed check below to get wrong. */
  decks: DeckSummary[];
  /** The whole view. Only the narrowing axes are read — see `hidden` below. */
  filters: DeckFilterState;
  /** Overridable so a test can assert the cap without inventing seven decks. */
  limit?: number;
};

/**
 * Is the strip suppressed by the current view?
 *
 * The rule is one sentence: **the strip is for an UNNARROWED page.** The moment
 * someone is looking for something specific — a search, a status, a folder, a
 * model, an updated window, the Trash — a second, differently-ordered set of
 * decks above the answer is noise at best and a contradiction at worst.
 *
 * It leans on `activeFilterCount` rather than a second notion of "filtered", so
 * a narrowing axis added later cannot forget to hide this. Sort, view mode and
 * tile size are deliberately NOT narrowing — they change how the same decks are
 * shown, and the strip is happy above any of them.
 */
export function recentStripHidden(filters: DeckFilterState): boolean {
  return activeFilterCount(filters) > 0;
}

/**
 * The decks in the strip, most recently opened first. Empty means render
 * nothing — an empty strip and a hidden strip are the same thing to the caller.
 *
 * A deck with no `lastOpenedAt` has never been opened since the stamp shipped
 * (it lives in the deck's own studio.json, written by the editor's open), so it
 * is out: this row answers "what were you just in", and a deck nobody has
 * opened has no answer. There is deliberately no time WINDOW here: coming back
 * after a fortnight away should still find your last six decks rather than an
 * empty heading.
 */
export function recentStripDecks({ decks, filters, limit = RECENT_STRIP_LIMIT }: RecentStripInput): DeckSummary[] {
  if (recentStripHidden(filters)) return [];
  return decks
    .filter((d) => typeof d.lastOpenedAt === 'number')
    .sort((a, b) => (b.lastOpenedAt as number) - (a.lastOpenedAt as number))
    .slice(0, Math.max(0, limit));
}
